import type { ChangeEvent } from "react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import * as Papa from "papaparse";

import { IconButton } from "@similarweb/ui-components";

import { ImportSource } from "app/components/import-source/ImportSource";
import { translate } from "app/filters/i18nFilter";
import type { IImportedKeywords, IManualKeywords } from "app/pages/digital-marketing/keywords-campaigns/shared/types";
import { allTrackers } from "app/services/track/track";
import { getCharsetEncoding } from "app/UtilitiesAndConstants/UtilityFunctions/getCharsetEncoding";

import type { MappingData } from "./ColumnMappingModal/ColumnMappingModal";
import { ColumnMappingModal } from "./ColumnMappingModal/ColumnMappingModal";
import { parseImportedKeywords } from "./ColumnMappingModal/columnMappingUtils";
import {
    TextAreaContainer,
    StyledBottomContainer,
    StyledImportSourcesList,
    StyledChildrenContainer,
    DropOverlay,
    ImportingOverlay,
} from "./styles";
import type { ITextareaInputRef } from "./TextareaInput";
import { TextareaInput } from "./TextareaInput";
import { parseKeywordsFromText } from "./utils/parseKeywordsFromText";

interface IKeywordsTextareaProps {
    manualSource: IManualKeywords;
    importSources: IImportedKeywords[];
    isImportFromGscDisabled?: boolean;
    height?: string;
    onManualInput: (manualKeywords: IManualKeywords) => void;
    onImportFromGscClick?: () => void;
    children: React.ReactNode;
    isImporting?: boolean;
    onCsvImport?: (importedKeywords: IImportedKeywords) => void;
    onRemoveImport?: (importId: string) => void;
    onImportError?: () => void;
}

export const KeywordsTextarea = ({
    manualSource,
    importSources,
    onManualInput,
    onCsvImport,
    onRemoveImport,
    onImportError,
    children,
    height,
    isImporting,
    isImportFromGscDisabled,
    onImportFromGscClick,
}: IKeywordsTextareaProps) => {
    const [parseResult, setParseResult] = useState<{
        fileName: string;
        result: Papa.ParseResult<unknown>;
    } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isImportingData, setIsImportingData] = useState(false);
    const textareaRef = useRef<ITextareaInputRef>(null);
    const csvImportInput = useRef<HTMLInputElement>(null);
    const isProcessingChange = useRef(false);

    useEffect(() => {
        if (isProcessingChange.current) {
            return;
        }

        if (textareaRef.current) {
            const keywordLines = manualSource.keywords.map((kw) =>
                kw.tags && kw.tags.length > 0 ? `${kw.value}, ${kw.tags.join(", ")}` : kw.value,
            );

            textareaRef.current.updateLines(keywordLines);
        }
    }, [manualSource]);

    const processCsvFile = useCallback(
        async (file: File) => {
            if (!(file.type === "text/csv" || file.name.endsWith(".csv"))) {
                onImportError?.();
                return;
            }

            const encoding = await getCharsetEncoding(file);

            Papa.parse(file, {
                encoding,
                complete: (result) => {
                    allTrackers.dispatchTrackEvent("Popup", "open", "Import CSV/Column mapping");

                    setParseResult({
                        result,
                        fileName: file.name,
                    });
                },
            });
        },
        [onImportError],
    );

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            if (!isDragging) {
                setIsDragging(true);
            }
        },
        [isDragging],
    );

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const files = e.dataTransfer.files;
            if (files?.length > 0) {
                void processCsvFile(files[0]);
            }
        },
        [processCsvFile],
    );

    const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
            setIsDragging(false);
        }
    }, []);

    const handleTextChange = useCallback(
        (value: string) => {
            try {
                isProcessingChange.current = true;
                const updatedManualSource = parseKeywordsFromText(value, manualSource);
                onManualInput(updatedManualSource);
            } catch {
                isProcessingChange.current = false;
            }
        },
        [manualSource, onManualInput],
    );

    const handleFileOnChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const files = (e.target as HTMLInputElement).files;
            if (!files?.length) {
                return;
            }

            processCsvFile(files[0]).catch(() => {
                onImportError?.();
            });
        },
        [onImportError, processCsvFile],
    );

    const handleImportClick = useCallback(() => {
        if (!csvImportInput.current) {
            return;
        }
        csvImportInput.current.click();
        // Reset the input value to unlock triggering input onChange for the same file twice
        csvImportInput.current.value = "";
        allTrackers.dispatchTrackEvent("Button", "click", "Import CSV/Upload CSV");
        allTrackers.dispatchTrackEvent("Button", "click", "Default wizard/Import CSV");
    }, []);

    const onColumnMappingModalConfirm = useCallback(
        (mappingData: MappingData) => {
            setParseResult(null);

            setIsImportingData(true);

            const { parsedData: data, startRow, mappingConfig, id, fileName } = mappingData;

            setTimeout(() => {
                try {
                    const importedKeywords = parseImportedKeywords(data, startRow, mappingConfig, id, fileName);
                    onCsvImport?.(importedKeywords);
                } finally {
                    setIsImportingData(false);
                }
            }, 0);
        },
        [onCsvImport],
    );

    const placeholderText: string = translate("campaigns.keywordsStep.inputBox.placeholder");

    return (
        <TextAreaContainer
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <TextareaInput
                ref={textareaRef}
                placeholder={placeholderText}
                height={height}
                onTextChange={handleTextChange}
            />

            {isDragging ? <DropOverlay>{translate("segmentWizard.csvModal.dropHere")}</DropOverlay> : null}
            {isImporting || isImportingData ? (
                <ImportingOverlay>{translate("campaigns.keywordsStep.inputBox.importing")}</ImportingOverlay>
            ) : null}

            <StyledBottomContainer>
                <StyledImportSourcesList onClick={() => textareaRef.current?.focus()}>
                    {importSources?.map(({ type, name, id, keywords, locations, importedKeywordsCount }) => (
                        <ImportSource
                            key={id}
                            type={type}
                            name={name ?? ""}
                            onRemove={() => onRemoveImport?.(id)}
                            keywordsAmount={importedKeywordsCount ?? keywords.length}
                            locations={locations}
                        />
                    ))}
                </StyledImportSourcesList>
                <StyledChildrenContainer>
                    {children}
                    <IconButton
                        type="flat"
                        iconName="upload"
                        onClick={handleImportClick}
                        data-automation="keywords-step-import-csv-button"
                    >
                        {translate("seo.keywordsCampaign.wizard.addingKeywords.iconText")}
                    </IconButton>
                    {onImportFromGscClick ? (
                        <IconButton
                            type="flat"
                            iconName="google-search"
                            isDisabled={isImportFromGscDisabled}
                            onClick={onImportFromGscClick}
                            data-automation="keywords-step-import-from-gsc-button"
                        >
                            {translate("seo.keywordsCampaign.wizard.addingKeywords.importFromGscButton")}
                        </IconButton>
                    ) : null}
                </StyledChildrenContainer>
            </StyledBottomContainer>

            {parseResult ? (
                <ColumnMappingModal
                    fileName={parseResult.fileName}
                    parseResult={parseResult.result}
                    onConfirm={onColumnMappingModalConfirm}
                    onCancel={() => setParseResult(null)}
                />
            ) : null}
            <input
                hidden
                aria-label="CSV file input"
                ref={csvImportInput}
                type="file"
                accept="text/csv"
                id="csv-file-input"
                onChange={handleFileOnChange}
            />
        </TextAreaContainer>
    );
};
